import React, { CSSProperties } from "react";
import { GalleryImage } from "./types";

interface GalleryExportProps {
  images?: GalleryImage[];
  columns?: number;
  gap?: number;
  showCaptions?: boolean;
  enableLightbox?: boolean;
}

export const GalleryExport: React.FC<GalleryExportProps> = ({
  images = [],
  columns = 3,
  gap = 16,
  showCaptions = false,
  enableLightbox = true,
}) => {
  const safeImages = Array.isArray(images) ? images : [];

  const gridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: `${gap}px`,
  };

  const itemStyle: CSSProperties = {
    position: "relative",
    aspectRatio: "1",
    overflow: "hidden",
    borderRadius: "8px",
    cursor: enableLightbox ? "pointer" : "default",
  };

  const captionStyle: CSSProperties = {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "white",
    padding: "8px",
    fontSize: "0.875rem",
  };

  return (
    <div style={{ padding: "16px" }} className="gallery-container" data-lightbox={enableLightbox}>
      <div className="gallery-grid" style={gridStyle}>
        {safeImages.map((image, index) => (
          <div
            key={index}
            className="gallery-item"
            style={itemStyle}
            data-index={index}
            data-src={image.src}
            data-alt={image.alt || ""}
          >
            <img
              src={image.src}
              alt={image.alt}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.3s ease",
              }}
            />
            {showCaptions && image.caption && <div style={captionStyle}>{image.caption}</div>}
          </div>
        ))}
      </div>

      {/* Lightbox Modal - hidden by default, shown via JS */}
      {enableLightbox && safeImages.length > 0 && (
        <div
          className="gallery-lightbox"
          style={{
            display: "none",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Close button */}
          <button
            className="lightbox-close"
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              color: "white",
              fontSize: "2rem",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              lineHeight: 1,
              zIndex: 10,
            }}
          >
            ×
          </button>

          {/* Previous button */}
          <button
            className="lightbox-prev"
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              fontSize: "3rem",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              lineHeight: 1,
              zIndex: 10,
            }}
          >
            ‹
          </button>

          {/* Image container */}
          <img
            className="lightbox-image"
            src=""
            alt=""
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />

          {/* Next button */}
          <button
            className="lightbox-next"
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              fontSize: "3rem",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              lineHeight: 1,
              zIndex: 10,
            }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};
